import React, { useEffect } from 'react';
import { FileSpreadsheet, Image, File, X, Upload, ExternalLink } from 'lucide-react';
import { formatCurrency, parseCurrency } from '../../utils/formatting';

interface Step4Props {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onMAOUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDocUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeMAOReport: (index: number) => void;
  removePhoto: (index: number) => void;
  removeDoc: (index: number) => void;
  isUploading: boolean;
  onToggleMaoNotAvailable: () => void;
  onTogglePhotosNotAvailable: () => void;
}

export function Step4FinancialDocs({
  formData,
  onChange,
  onMAOUpload,
  onPhotoUpload,
  onDocUpload,
  removeMAOReport,
  removePhoto,
  removeDoc,
  isUploading,
  onToggleMaoNotAvailable,
  onTogglePhotosNotAvailable
}: Step4Props) {
  // Listen for MAO PDF from integrated calculator
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // No origin validation needed - same origin (served from /mao-calculator/)

      if (event.data.type === 'MAO_PDF_GENERATED') {
        const { pdfBlob, fileName } = event.data;

        // Convert blob to File object
        const file = new File([pdfBlob], fileName || 'MAO Calculations.pdf', {
          type: 'application/pdf'
        });

        // Create synthetic event to trigger onMAOUpload
        const syntheticEvent = {
          target: {
            files: [file]
          }
        } as any;

        onMAOUpload(syntheticEvent);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onMAOUpload]);

  const openIntegratedMAO = () => {
    try {
      const { propertyStreet, propertyLine2, propertyCity, propertyState, propertyZip, taxSaleAmount } = formData;
      const addressParts = [propertyStreet, propertyLine2, propertyCity, propertyState, propertyZip].filter(Boolean);
      const params = new URLSearchParams();
      if (addressParts.length > 0) {
        params.set('address', addressParts.join(', '));
      }
      if (taxSaleAmount) {
        params.set('taxSaleAmount', taxSaleAmount);
      }
      const qs = params.toString();
      const url = qs ? `/mao-calculator/index.html?${qs}` : '/mao-calculator/index.html';
      const maoWindow = window.open(url, 'MAO Calculator', 'width=1200,height=800,scrollbars=yes,resizable=yes');

      // Owner PII goes via postMessage only — never in the URL
      if (maoWindow) {
        const prefill = {
          type: 'PORTAL_PREFILL',
          ownerFirstName: formData.ownerFirstName,
          ownerLastName: formData.ownerLastName,
          ownerPhone: formData.ownerPhone,
          ownerEmail: formData.ownerEmail,
        };
        setTimeout(() => {
          try { maoWindow.postMessage(prefill, window.location.origin); } catch { /* window closed */ }
        }, 800);
        setTimeout(() => {
          try { maoWindow.postMessage(prefill, window.location.origin); } catch { /* window closed */ }
        }, 2000);
      }
    } catch {
      window.open('/mao-calculator/index.html', 'MAO Calculator', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    }
  };

  const openZillow = () => {
    // Open Zillow in a new separate window. If the partner has already entered the
    // property address (Step 2), deep-link to that property; otherwise open zillow.com.
    const { propertyStreet, propertyCity, propertyState, propertyZip } = formData;
    const fullAddress = [propertyStreet, propertyCity, propertyState, propertyZip]
      .filter(Boolean)
      .join(' ')
      .trim();
    const url = propertyStreet && propertyStreet.trim()
      ? `https://www.zillow.com/homes/${encodeURIComponent(fullAddress)}_rb/`
      : 'https://www.zillow.com';
    window.open(url, 'ZillowStreetView', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow user to type, format on blur
    onChange(e);
  };

  const handleCurrencyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const formatted = formatCurrency(parseCurrency(value));
      onChange({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          value: formatted
        }
      } as any);
    }
  };
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
        <FileSpreadsheet className="w-5 h-5 text-tdb-accent" />
        <span>Financial Analysis & Documents</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            After Repair Value (ARV)
          </label>
          <p className="text-xs text-gray-500 mb-2">Estimated value of property in fixed up condition</p>
          <input
            type="text"
            name="arv"
            value={formData.arv}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$200,000"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Suggested Offer *
          </label>
          <p className="text-xs text-gray-500 mb-2">Your recommended offer based on MAO analysis</p>
          <input
            type="text"
            name="suggestedOffer"
            value={formData.suggestedOffer}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$125,000"
          />
        </div>
      </div>

      {/* MAO Report Upload */}
      <div>
        <label className="block text-sm font-semibold text-tdb-primary mb-2">
          Submit your Maximum Allowable Offer Report {!formData.maoNotAvailable && '*'}
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Complete an MAO at{' '}
          <a
            href="https://mao.noflippingexcuses.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-tdb-accent hover:text-tdb-secondary font-semibold underline"
          >
            https://mao.noflippingexcuses.com
          </a>
          {' '}or use MAO Spreadsheet. Accepted: jpg, gif, png, pdf, doc, docx. Max 1 GB, up to 5 files.
        </p>

        {/* N/A checkbox */}
        <label className="inline-flex items-center gap-2 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={formData.maoNotAvailable}
            onChange={onToggleMaoNotAvailable}
            className="w-4 h-4 rounded border-gray-300 text-tdb-accent focus:ring-tdb-accent"
          />
          <span className="text-sm text-gray-600">N/A — I don't have this yet. I'll attach it later.</span>
        </label>

        {!formData.maoNotAvailable && (
          <>
            {/* Button to open integrated MAO calculator */}
            <div className="mb-4">
              <button
                type="button"
                onClick={openIntegratedMAO}
                className="inline-flex items-center px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-secondary transition-all font-semibold text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Don't have an MAO? Create one here
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Opens MAO calculator in new window. PDF will auto-attach when you finish.
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-tdb-accent transition-all">
              <input
                type="file"
                id="mao-upload"
                multiple
                accept=".jpg,.gif,.png,.pdf,.doc,.docx"
                onChange={onMAOUpload}
                className="hidden"
                disabled={isUploading}
              />
              <label htmlFor="mao-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-tdb-primary mb-1">
                  Upload MAO Report
                </p>
                <p className="text-xs text-gray-500">
                  Click to browse files
                </p>
              </label>
            </div>
          </>
        )}

        {formData.maoReportUrls.length > 0 && (
          <div className="space-y-2 mt-3">
            {formData.maoReportUrls.map((url: string, index: number) => {
              const fileName = url.split('/').pop()?.split('?')[0] || `MAO Report ${index + 1}`;
              const localFile = formData.maoReports[index - (formData.maoReportUrls.length - formData.maoReports.length)];
              return (
                <div key={index} className="bg-gray-50 p-3 rounded-xl flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileSpreadsheet className="w-6 h-6 text-tdb-accent" />
                    <div>
                      <p className="font-semibold text-sm text-tdb-primary">{localFile ? localFile.name : fileName}</p>
                      <p className="text-xs text-gray-500">
                        {localFile ? `${(localFile.size / 1024 / 1024).toFixed(2)} MB` : 'Uploaded'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMAOReport(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Property Photos Upload */}
      <div>
        <label className="block text-sm font-semibold text-tdb-primary mb-2">
          Property Photos
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Submit up to 20 photos: front & back view, main rooms, areas needing repair.
          JPG/PNG only, 50 MB max per file.
        </p>

        {/* N/A checkbox */}
        <label className="inline-flex items-center gap-2 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={formData.photosNotAvailable}
            onChange={onTogglePhotosNotAvailable}
            className="w-4 h-4 rounded border-gray-300 text-tdb-accent focus:ring-tdb-accent"
          />
          <span className="text-sm text-gray-600">N/A — I don't have photos yet. I'll attach them later.</span>
        </label>

        {!formData.photosNotAvailable && (
          <>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-tdb-accent transition-all">
              <input
                type="file"
                id="photo-upload"
                multiple
                accept="image/jpeg,image/png,image/jpg"
                onChange={onPhotoUpload}
                className="hidden"
                disabled={isUploading}
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Image className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-tdb-primary mb-1">
                  Upload Property Photos
                </p>
                <p className="text-xs text-gray-500">
                  Click to browse images
                </p>
              </label>
            </div>

            {/* Zillow street view helper */}
            <div className="mt-4">
              <button
                type="button"
                onClick={openZillow}
                className="inline-flex items-center px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-secondary transition-all font-semibold text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View on Zillow
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Don't have property photos? Visit Zillow to grab the street view of the property and attach it here.
              </p>
            </div>
          </>
        )}

        {formData.propertyPhotos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            {formData.propertyPhotos.map((photo: File, index: number) => (
              <div key={index} className="relative group">
                <img
                  src={URL.createObjectURL(photo)}
                  alt={`Property ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Additional Documents Upload */}
      <div>
        <label className="block text-sm font-semibold text-tdb-primary mb-2">
          Contact Library / Additional Documents
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Submit up to 20 files related to the property. PDF/DOC/DOCX, 50 MB max per file.
        </p>
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-tdb-accent transition-all">
          <input
            type="file"
            id="doc-upload"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={onDocUpload}
            className="hidden"
            disabled={isUploading}
          />
          <label htmlFor="doc-upload" className="cursor-pointer">
            <File className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-sm font-semibold text-tdb-primary mb-1">
              Upload Documents
            </p>
            <p className="text-xs text-gray-500">
              Tax records, title docs, etc.
            </p>
          </label>
        </div>

        {formData.additionalDocs.length > 0 && (
          <div className="space-y-2 mt-3">
            {formData.additionalDocs.map((doc: File, index: number) => (
              <div key={index} className="bg-gray-50 p-3 rounded-xl flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <File className="w-6 h-6 text-tdb-accent" />
                  <div>
                    <p className="font-semibold text-sm text-tdb-primary">{doc.name}</p>
                    <p className="text-xs text-gray-500">
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeDoc(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {isUploading && (
        <div className="text-center bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tdb-accent mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Uploading files...</p>
        </div>
      )}
    </div>
  );
}
